/**
 * PortalAuth — wraps the new RetailCliQ ERP login portal as the auth gate
 * for the main ERP application. On successful login, calls onSuccess(token, globalContext, userSummary)
 * so that the existing MainApp can take over.
 */
import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';

import { ThemeProvider } from './portal/context/ThemeContext';
import { AuthProvider, useAuth } from './portal/context/AuthContext';
import { GlobalizationProvider } from './portal/context/GlobalizationContext';
import './portal/portal.css';
// I18nProvider is provided by the outer App() wrapper — no need to re-mount here.

import LoginPage from './portal/pages/LoginPage';
import LoginPageV2 from './portal/pages/LoginPageV2';
import LoginPageV3 from './portal/pages/LoginPageV3';
import IndustryLoginPage from './portal/pages/IndustryLoginPage';
import MFAPage from './portal/pages/MFAPage';
import ForgotPasswordPage from './portal/pages/ForgotPasswordPage';
import ChangePasswordPage from './portal/pages/ChangePasswordPage';
import SignupPage from './portal/pages/SignupPage';

function RequireTempToken({ children }) {
  const { tempToken } = useAuth();
  if (!tempToken) return <Navigate to="/login" replace />;
  return children;
}

function RequireAccessToken({ children }) {
  const { accessToken } = useAuth();
  if (!accessToken) return <Navigate to="/login" replace />;
  return children;
}

function PortalRouterInner({ onSuccess }) {
  const { accessToken, userSummary } = useAuth();
  const onSuccessRef = React.useRef(onSuccess);
  React.useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  useEffect(() => {
    if (accessToken && userSummary) {
      let gc = null;
      try {
        const raw = localStorage.getItem('rcq_global_context') || sessionStorage.getItem('rcq_global_context');
        if (raw) gc = JSON.parse(raw);
      } catch (_) { /* silent */ }
      // Bridge to existing MainApp via stable ref (avoid re-firing on prop identity changes)
      onSuccessRef.current && onSuccessRef.current(accessToken, gc, userSummary);
    }
  }, [accessToken, userSummary]);

  // Once authenticated, hold a clean transitional UI so the wildcard route
  // doesn't bounce navigate('/select-app') back to /login during the swap.
  if (accessToken && userSummary) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white dark:bg-[#080808]" data-testid="portal-handoff">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-[#3DD56D]/30 border-t-[#3DD56D] rounded-full rcq-spin" />
          <p className="text-sm text-gray-700 dark:text-neutral-300">Loading workspace…</p>
        </div>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/login/v2" element={<LoginPageV2 />} />
      <Route path="/login/v3" element={<LoginPageV3 />} />
      <Route path="/login/industry/:industryId" element={<IndustryLoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/change-password" element={<RequireAccessToken><ChangePasswordPage /></RequireAccessToken>} />
      <Route path="/mfa" element={<RequireTempToken><MFAPage /></RequireTempToken>} />
      <Route path="/signup" element={<SignupPage />} />
      <Route path="*" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}

export default function PortalAuth({ onSuccess }) {
  return (
    <div className="rcq-portal">
      <ThemeProvider>
        <GlobalizationProvider>
          <AuthProvider>
            <BrowserRouter>
              <Toaster richColors position="top-right" expand={false} closeButton toastOptions={{ duration: 4000 }} />
              <PortalRouterInner onSuccess={onSuccess} />
            </BrowserRouter>
          </AuthProvider>
        </GlobalizationProvider>
      </ThemeProvider>
    </div>
  );
}
